import { QuestionCircleOutlined } from "@ant-design/icons";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  Collapse,
  Descriptions,
  FloatButton,
  Popover,
  Progress,
  Result,
  Row,
  Statistic,
  Tabs,
} from "antd";
import config from "app/envConfig";
import { formatCurrency } from "common/utils";
import currency from "currency.js";
import { useFetchDebtorProfileQuery } from "features/accountsTable/agencyPortal/accountsTableAPI";
import BankruptciesTable from "features/bankruptcies/bankruptciesTable";
import { useFetchCommunicationLimitsForDebtorQuery } from "features/communicationLimitsTable/communicationLimitsApi";
import { DebtorAccountsTable } from "features/debtorAccountsTable";
import { useFetchDebtorAccountsQuery } from "features/debtorAccountsTable/agencyPortal/debtorAccountsAPI";
import DebtorCreditReportsTable from "features/debtorCreditReportTable/debtorCreditReportTable";
import AddCostsTab from "features/debtorProfile/components/AddCostsTab";
import SendMessagePopover from "features/debtorProfile/components/SendMessagePopover";
import { FloatingCallPopover } from "features/internalDialer";
import SystemLogTable from "features/debtorProfile/components/systemLogTable";
import UnifiedActivityFeed from "features/debtorProfile/components/unifiedActivityFeed";
import { CommunicationsTable } from "features/communicationsTable";
import { SIDER_ID } from "features/debtorSider/agencyPortal/debtorSider";
import DocumentsTable from "features/documentsTable/documentsTable";
import { GarnishmentsTable } from "features/garnishments";
import LegalInfo from "features/legal/legalInfo";
import { DebtorAssetsInfoTable } from "features/debtorAssets";
import PaymentCard from "features/payments";
import { resetMakeAPayment } from "features/payments/paymentsSlice";
import { PERMISSIONS, useAuthorization } from "features/permissions";
import useFeatureFlags from "features/permissions/hooks/useFeatureFlags";
import { PaymentHistory, ScheduledPayments } from "features/scheduledPayments";
import { useAppearance } from "providers/appearanceProvider";
import CompactModeToggle from "components/compactModeToggle";
import { useCompactMode } from "providers/compactModeProvider";
import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { Link, useParams } from "react-router-dom";
import styled from "styled-components";
import { useDocumentTitle } from "usehooks-ts";
import { PaymentMethodsTable } from "features/paymentMethods";
import { useFetchAgencyQuery } from "features/basicInfo/agencyPortal/agencySettingsAPI";
import { MilitaryTable } from "features/military";
import PacketedCollectionRecordsModal from "features/debtorProfile/components/packetedCollectionRecordsModal";

const { Panel } = Collapse;

const StyledQuestionCircleOutlined = styled(QuestionCircleOutlined)`
  color: #1677ff;
  margin-left: 4px;
`;

const StyledStatistic = styled(Statistic)`
  margin-right: 36px;
`;

const StyledCard = styled(Card)`
  .ant-card-body {
    padding-top: 0;
    padding-bottom: 16px;
  }
  margin-top: 16px;
`;

const StyledCollapse = styled(Collapse)`
  margin-top: 16px;
  background-color: #fff;
`;

const StyledPanel = styled(Panel)`
  .ant-collapse-header {
    align-items: center !important;
    padding-bottom: 0 !important;
    padding-top: 0 !important;
  }

  .ant-collapse-content-box {
    padding-top: 0 !important;
  }
`;

const StyledLimitRuleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
`;

const StyledLimitRuleLabel = styled.span`
  font-size: 12px;
  white-space: nowrap;
  min-width: 80px;
`;

const StyledLimitProgress = styled(Progress)`
  flex: 1;
  min-width: 60px;

  .ant-progress-text {
    display: none;
  }
`;

const StyledLimitCount = styled.span`
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
  white-space: nowrap;
`;

const StyledDiv = styled.div`
  margin-left: ${(props) =>
    `${
      // @ts-ignore
      props.$hidePanel || props.$siderCollapsed ? 0 : 400
    }px`};
  transition: margin-left 0.25s ease;
`;

const StyledButton = styled(Button)`
  margin-left: 8px;
`;

const StyledLink = styled(Link)`
  margin-left: 0px;
  padding-left: 0px;
`;

const StyledToggleRow = styled.div`
  width: 100%;
  margin-bottom: 8px;
`;

function DebtorProfile({ isSiderCollapsed = false }) {
  const { debtorId } = useParams();
  const [isPacketedDebtorsModalVisible, setIsPacketedDebtorsModalVisible] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const { data: isPaymentHistoryAuthorized } = useAuthorization(PERMISSIONS.PAYMENT__GET);
  const { data: isUpcomingPaymentsAuthorized } = useAuthorization(PERMISSIONS.PAYMENT__GET);
  const { data: isMakePaymentAuthorized } = useAuthorization(PERMISSIONS.PAYMENT__CREATE);
  const { data: isPaymentMethodsAuthorized } = useAuthorization(PERMISSIONS.PAYMENT__GET);
  const { data: isBankruptcyAuthorized } = useAuthorization(PERMISSIONS.DEBTOR_BANKRUPTCY__GET);
  const { data: isMilitaryAuthorized } = useAuthorization(PERMISSIONS.DEBTOR_MILITARY__GET);
  const { data: canViewCommunicationLimit } = useAuthorization(
    PERMISSIONS.DEBTOR_PROFILE_COMM_LIMIT__CAN_VIEW,
  );
  const { data: isAdditionalCostAuthorized } = useAuthorization(
    PERMISSIONS.ACCOUNT__ADDITIONAL_COSTS,
  );
  const { data: isCommunicationRuleAuthorized } = useAuthorization(
    PERMISSIONS.COMMUNICATION_RULE__GET,
  );
  const { data: isDebtorCreditReportAuthorized } = useAuthorization(
    PERMISSIONS.DEBTOR_CREDIT_REPORT__GET,
  );
  const { data: isDebtorAssetAuthorized } = useAuthorization(PERMISSIONS.DEBTOR_ASSET__GET);
  const { data: isSystemLogAuthorized } = useAuthorization(PERMISSIONS.SYSTEM_LOG__GET);
  const { appearance } = useAppearance();
  const { isCompactMode } = useCompactMode();

  const {
    data: [hasComplianceFeature],
  } = useFeatureFlags(["compliance"]);
  const { data: agencySettings } = useFetchAgencyQuery();

  const {
    data: debtorPersonalInfo,
    isLoading: isDebtorPersonalInfoLoading,
    isFetching: isDebtorPersonalInfoFetching,
    isError: isDebtorPersonalInfoError,
  } = useFetchDebtorProfileQuery({ debtorId });

  const {
    data: debtorAccounts,
    isLoading,
    isFetching,
  } = useFetchDebtorAccountsQuery(
    { debtorId },
    {
      skip:
        !debtorId ||
        isDebtorPersonalInfoLoading ||
        isDebtorPersonalInfoError ||
        isDebtorPersonalInfoFetching,
    },
  );

  const activeAccounts = useMemo(
    () =>
      debtorAccounts?.filter(
        (account) => !(account.isClosed || account.isPaidInFull || account.isSettled),
      ),
    [debtorAccounts],
  );

  // @ts-ignore
  const { data: communicationLimitRules } = useFetchCommunicationLimitsForDebtorQuery(debtorId, {
    skip:
      !isCommunicationRuleAuthorized ||
      !hasComplianceFeature ||
      !canViewCommunicationLimit ||
      isDebtorPersonalInfoLoading ||
      isDebtorPersonalInfoFetching ||
      isDebtorPersonalInfoError,
  });
  const filteredRules =
    communicationLimitRules?.filter((each) => each.communicationCount >= each.rule.maxAllowed) ??
    [];
  const isPaymentTabsShown =
    isPaymentHistoryAuthorized || isUpcomingPaymentsAuthorized || isMakePaymentAuthorized;

  const dispatch = useDispatch();

  const isDevMode = config.aktosEnv !== "prod";
  const prefix = isDevMode ? `[${config.aktosEnv}] ` : "";
  const debtorName = debtorPersonalInfo?.name ?? "";
  const debtorExternalId = debtorPersonalInfo?.externalId
    ? ` ${debtorPersonalInfo.externalId}`
    : "";
  const debtorTitle = debtorName
    ? `${prefix}${debtorName}${debtorExternalId} - Aktos`
    : `${prefix}Aktos`;
  useDocumentTitle(debtorTitle, { preserveTitleOnUnmount: false });

  // Reset "Make a Payment" progress on new Debtor
  useEffect(() => {
    dispatch(resetMakeAPayment());
  }, [dispatch]);

  const overViewTabs = [
    {
      label: "Accounts",
      key: "accounts",
      children: <DebtorAccountsTable debtorId={debtorId} />,
    },
    {
      label: "Lawsuits",
      key: "lawsuits",
      children: <LegalInfo debtorId={debtorId} />,
    },
    {
      label: "Documents",
      key: "documents",
      children: <DocumentsTable debtorId={debtorId} />,
    },
    isAdditionalCostAuthorized && {
      label: "Additional Costs",
      key: "additionalCosts",
      children: <AddCostsTab />,
    },
    isBankruptcyAuthorized && {
      label: "Bankruptcy",
      key: "bankruptcy",
      children: <BankruptciesTable debtorId={debtorId} />,
    },
    isMilitaryAuthorized && {
      label: "Military",
      key: "military",
      children: <MilitaryTable debtorId={debtorId} />,
    },
    {
      label: "Garnishments",
      key: "garnishments",
      children: <GarnishmentsTable debtorId={debtorId} />,
    },
    isDebtorAssetAuthorized && {
      label: "Assets",
      key: "assets",
      children: <DebtorAssetsInfoTable debtorId={debtorId} />,
    },
    isDebtorCreditReportAuthorized && {
      label: "Credit Report",
      key: "creditReport",
      children: <DebtorCreditReportsTable debtorId={debtorId} />,
    },
  ];

  const paymentTabs = [
    isMakePaymentAuthorized && {
      label: "Make a Payment",
      key: "paymentPlans",
      children: <PaymentCard />,
    },
    isUpcomingPaymentsAuthorized && {
      label: "Scheduled Payments",
      key: "scheduledPayments",
      children: <ScheduledPayments />,
    },
    isPaymentHistoryAuthorized && {
      label: "Payment History",
      key: "paymentHistory",
      children: <PaymentHistory />,
    },
    isPaymentMethodsAuthorized && {
      label: "Payment Methods",
      key: "paymentMethods",
      children: <PaymentMethodsTable />,
    },
  ];

  const balanceItems = [
    {
      key: "principal",
      label: "Principal",
      value: formatCurrency(
        activeAccounts?.reduce((acc, account) => acc.add(account.principalBalance), currency(0)),
      ),
    },
    {
      key: "interest",
      label: "Agency Interest",
      value: formatCurrency(
        activeAccounts?.reduce((acc, account) => acc.add(account.interestBalance), currency(0)),
      ),
    },
    {
      key: "clientInterest",
      label: "Client Interest",
      value: formatCurrency(
        activeAccounts?.reduce(
          (acc, account) => acc.add(account.clientInterestBalance),
          currency(0),
        ),
      ),
    },
    {
      key: "prejudgmentInterest",
      label: "Prejudgment Interest",
      value: formatCurrency(
        activeAccounts?.reduce(
          (acc, account) => acc.add(account.prejudgmentInterestBalance),
          currency(0),
        ),
      ),
    },
    {
      key: "totalFees",
      label: "Fees",
      value: formatCurrency(
        activeAccounts?.reduce((acc, account) => acc.add(account.totalFeesBalance), currency(0)),
      ),
    },
    {
      key: "total",
      label: <strong>Total</strong>,
      value: (
        <strong>
          {formatCurrency(
            activeAccounts?.reduce((acc, account) => acc.add(account.totalBalance), currency(0)),
          )}
        </strong>
      ),
    },
  ];

  if (isDebtorPersonalInfoError) {
    return (
      <Result
        status="404"
        title="No debtor found"
        subTitle="Please check the debtor ID and try again"
      />
    );
  }

  return (
    <>
      <StyledDiv
        // @ts-ignore
        $hidePanel={appearance?.items?.[SIDER_ID]?.hidePanel}
        $siderCollapsed={isSiderCollapsed}
      >
        <FloatButton.Group>
          <FloatingCallPopover />
          {agencySettings?.isInternalMessagingEnabled && debtorPersonalInfo && (
            <SendMessagePopover debtorPersonalInfo={debtorPersonalInfo} debtorId={debtorId} />
          )}
        </FloatButton.Group>
        <FloatButton.BackTop />
        <StyledToggleRow>
          <CompactModeToggle />
        </StyledToggleRow>
        {debtorPersonalInfo?.packetId && (
          <Alert
            type={
              debtorPersonalInfo?.packetMainDebtorId?.toString() !== debtorId?.toString()
                ? "error"
                : "warning"
            }
            showIcon
            message={
              <>
                {debtorPersonalInfo?.packetMainDebtorId?.toString() !== debtorId?.toString() ? (
                  <>
                    This debtor is in a packet with other debtors, but is not the main debtor.
                    Navigate to the
                    <StyledLink
                      to={`/debtors/${debtorPersonalInfo?.packetMainDebtorId}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {" "}
                      <span>main debtor</span>
                    </StyledLink>
                    .
                  </>
                ) : (
                  "This debtor is in a packet with other debtors."
                )}

                <StyledButton
                  type="primary"
                  onClick={() => {
                    setIsPacketedDebtorsModalVisible(true);
                  }}
                >
                  View/Manage Packeted Debtors
                </StyledButton>
              </>
            }
          />
        )}
        <StyledCard loading={isLoading || isFetching}>
          <h3>Overview</h3>
          <Row align="middle">
            <div data-testid="stat-total-balance">
              <StyledStatistic
                valueStyle={{
                  fontSize: "20px",
                  fontWeight: "bold",
                }}
                title={
                  <Row align="middle">
                    <div>Total Account Balance</div>
                    <Popover
                      content={
                        <Col>
                          <Descriptions bordered column={1} size="small">
                            {balanceItems?.map((item) => (
                              <Descriptions.Item key={item.key} label={item.label}>
                                {item.value}
                              </Descriptions.Item>
                            ))}
                          </Descriptions>
                        </Col>
                      }
                      title="Total Account Balance"
                      trigger="hover"
                      placement="right"
                    >
                      <StyledQuestionCircleOutlined />
                    </Popover>
                  </Row>
                }
                value={formatCurrency(
                  activeAccounts?.reduce(
                    (acc, account) => acc.add(account.totalBalance),
                    currency(0),
                  ),
                )}
                precision={2}
              />
            </div>
            <div data-testid="stat-active-accounts">
              <StyledStatistic
                valueStyle={{
                  fontSize: "20px",
                  fontWeight: "bold",
                }}
                title="# Active Accounts"
                value={activeAccounts?.length ?? 0}
              />
            </div>
          </Row>
        </StyledCard>
        <div>
          <div data-testid="section-collection-records">
            <StyledCollapse defaultActiveKey={[1]} bordered={false}>
              <StyledPanel header={<h3>Collection Records</h3>} key={1}>
                <Tabs items={overViewTabs} />
              </StyledPanel>
            </StyledCollapse>
          </div>
          {/* QA: compact mode renders section-activity; classic mode renders section-communications */}
          {isCompactMode ? (
            <div data-testid="section-activity">
              <StyledCollapse defaultActiveKey={[2]} bordered={false}>
                <StyledPanel header={<h3>Activity</h3>} key={2}>
                  <UnifiedActivityFeed
                    key={debtorId}
                    debtorId={debtorId}
                    isDebtorPacketed={!!debtorPersonalInfo?.packetId}
                    hasConsumerConsent={filteredRules.length === 0 || isChecked}
                    alertNode={
                      communicationLimitRules?.length > 0 ? (
                        <Alert
                          message={
                            filteredRules?.length > 0
                              ? "Warning: Communication Limit Reached"
                              : "Communication Limit Status"
                          }
                          description={
                            <Col>
                              {communicationLimitRules?.map((each) => (
                                <StyledLimitRuleRow key={each.rule.id}>
                                  <StyledLimitRuleLabel>
                                    {each.rule.name ?? `Rule ${each.rule.id}`}
                                  </StyledLimitRuleLabel>
                                  <StyledLimitProgress
                                    size="small"
                                    percent={(each.communicationCount / each.rule.maxAllowed) * 100}
                                    showInfo={false}
                                    status={
                                      each.communicationCount >= each.rule.maxAllowed
                                        ? "exception"
                                        : "normal"
                                    }
                                  />
                                  <StyledLimitCount>
                                    {each.communicationCount}/{each.rule.maxAllowed}
                                  </StyledLimitCount>
                                </StyledLimitRuleRow>
                              ))}
                              {filteredRules?.length > 0 && (
                                <Checkbox onChange={(e) => setIsChecked(e.target.checked)}>
                                  By checking this box, I acknowledge that I have obtained consumer
                                  consent.
                                </Checkbox>
                              )}
                            </Col>
                          }
                          type={filteredRules?.length > 0 ? "error" : "info"}
                          showIcon
                        />
                      ) : null
                    }
                  />
                </StyledPanel>
              </StyledCollapse>
            </div>
          ) : (
            <div data-testid="section-communications">
              <StyledCollapse defaultActiveKey={[2]} bordered={false}>
                <StyledPanel header={<h3>Communications</h3>} key={2}>
                  {communicationLimitRules?.length > 0 && (
                    <Alert
                      message={
                        filteredRules?.length > 0
                          ? "Warning: Communication Limit Reached"
                          : "Communication Limit Status"
                      }
                      description={
                        <Col>
                          {communicationLimitRules?.map((each) => (
                            <StyledLimitRuleRow key={each.rule.id}>
                              <StyledLimitRuleLabel>
                                {each.rule.name ?? `Rule ${each.rule.id}`}
                              </StyledLimitRuleLabel>
                              <StyledLimitProgress
                                size="small"
                                percent={(each.communicationCount / each.rule.maxAllowed) * 100}
                                showInfo={false}
                                status={
                                  each.communicationCount >= each.rule.maxAllowed
                                    ? "exception"
                                    : "normal"
                                }
                              />
                              <StyledLimitCount>
                                {each.communicationCount}/{each.rule.maxAllowed}
                              </StyledLimitCount>
                            </StyledLimitRuleRow>
                          ))}
                          {filteredRules?.length > 0 && (
                            <Checkbox onChange={(e) => setIsChecked(e.target.checked)}>
                              By checking this box, I acknowledge that I have obtained consumer
                              consent.
                            </Checkbox>
                          )}
                        </Col>
                      }
                      type={filteredRules?.length > 0 ? "error" : "info"}
                      showIcon
                    />
                  )}
                  <CommunicationsTable
                    key={debtorId}
                    debtorId={debtorId}
                    isDebtorPacketed={!!debtorPersonalInfo?.packetId}
                    hasConsumerConsent={filteredRules.length === 0 || isChecked}
                  />
                </StyledPanel>
              </StyledCollapse>
            </div>
          )}
          {/* QA: only rendered when the debtor has payment tabs enabled */}
          {isPaymentTabsShown && (
            <div data-testid="section-payments">
              <StyledCollapse defaultActiveKey={[3]} bordered={false}>
                <StyledPanel header={<h3>Payments</h3>} key={3}>
                  <Tabs items={paymentTabs} />
                </StyledPanel>
              </StyledCollapse>
            </div>
          )}
          {/* QA: only rendered in classic mode when user has system log permission */}
          {!isCompactMode && isSystemLogAuthorized && (
            <div data-testid="section-system-log">
              <StyledCollapse bordered={false}>
                <StyledPanel header={<h3>System Log</h3>} key={4}>
                  <SystemLogTable debtorId={debtorId} />
                </StyledPanel>
              </StyledCollapse>
            </div>
          )}
        </div>
      </StyledDiv>
      {isPacketedDebtorsModalVisible && (
        <PacketedCollectionRecordsModal
          packetId={debtorPersonalInfo?.packetId}
          open={isPacketedDebtorsModalVisible}
          onCancel={() => setIsPacketedDebtorsModalVisible(false)}
        />
      )}
    </>
  );
}

export default DebtorProfile;
